import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

// Define error type for better error handling
interface ServiceError extends Error {
  code?: string;
  statusCode?: number;
  details?: any;
}

// Test mode toggle
let testModeEnabled = false;

export const getTestModeEnabled = () => {
  return testModeEnabled;
};

export const toggleTestMode = (enabled: boolean) => {
  testModeEnabled = enabled;
  console.log(`Test mode ${enabled ? 'enabled' : 'disabled'}`);
  return testModeEnabled;
};

// Define interface for the generation status
export interface GenerationStatus {
  // 'reviewing' (ADR-0138): the package passed the structural gate and is held for the automated quality review before it is released.
  // 'held_for_review' (ADR-0178): a paid order the order preflight holds for a human (a cast above 30, ADR-0170); the trigger stores it,
  // the game page shows "submitted to our team", and a release flips it back to in_progress.
  status: 'not_started' | 'in_progress' | 'reviewing' | 'held_for_review' | 'completed' | 'failed';
  progress: number;
  currentStep: string;
  // ISO timestamp when generation started; used by the progress UI to anchor its
  // time-based simulated creep so a page refresh mid-generation resumes accurately.
  startedAt?: string | null;
  resumable?: boolean;
  sections?: {
    hostGuide?: boolean;
    characters?: boolean;
    clues?: boolean;
    inspectorScript?: boolean;
    characterMatrix?: boolean;
    [key: string]: boolean | undefined;
  };
}

// Helper function to detect current domain
const getCurrentDomain = () => {
  // Client-side detection
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  
  // Server-side detection for different environments
  if (process.env.VERCEL_URL) {
    // Vercel automatically sets this in production and preview deployments
    return `https://${process.env.VERCEL_URL}`;
  }
  
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3000';
  }
  
  // Fallback to your production domain
  return 'https://www.mysterymaker.party';
};

// Enhanced package generation with domain detection
export async function generateCompletePackage(mysteryId: string, testMode = false): Promise<string> {
  let packageId: string | undefined;

  try {
    console.log("Starting package generation for conversation ID:", mysteryId);

    // Prevent duplicate generation attempts. Read-only first-line UX check --
    // the server's atomic claim (claim_package_for_generation, ADR-0104) is the
    // real enforcement. This must stay read-only: it used to also write
    // generation_status='in_progress' to the DB before calling the edge
    // function below, which meant the server's claim always saw a row this
    // same call had *just* marked in_progress and rejected it with 409 on
    // every attempt -- a guaranteed self-collision, not a race (ADR-0104
    // Addendum 2, 2026-08-23).
    const { data: existingPackage, error: existingPackageErr } = await supabase
      .from("mystery_packages")
      .select("id, generation_status, generation_started_at")
      .eq("conversation_id", mysteryId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existingPackageErr) {
      console.error("Error checking for existing generation:", existingPackageErr);
    } else if (existingPackage?.generation_status?.status === 'completed') {
      console.log("Package already generated — skipping duplicate generation");
      return "already_completed";
    } else if (existingPackage?.generation_status?.status === 'in_progress') {
      console.warn("Generation already in progress – aborting duplicate webhook call");
      return "already_in_progress";
    }
    if (existingPackage?.generation_status?.status === 'held_for_review') {
      // Already waiting for a human (ADR-0178): do not run the trigger's extraction and preflight again for every click.
      return "held_for_review";
    }

    packageId = existingPackage?.id;

    // Get conversation data
    const { data: conversation, error: conversationError } = await supabase
      .from("conversations")
      .select("*, messages!fk_messages_conversation_id(*), user_id, theme, player_count, script_type, has_accomplice, additional_details")
      .eq("id", mysteryId)
      .single();

    if (conversationError || !conversation) {
      console.error("Error fetching conversation:", conversationError);
      throw new Error("Failed to fetch conversation data");
    }

    console.log(`Found conversation with ${conversation.messages?.length || 0} messages`);

    // Note: no pre-write of generation_status here. claim_package_for_generation
    // (called inside mystery-webhook-trigger below) is the sole writer of the
    // 'in_progress' state -- it creates the mystery_packages row if none exists
    // yet, or claims an existing one. packageId is resolved after the call below.

    // Prepare conversation content
    const conversationContent = conversation.messages
      ? conversation.messages.map((msg: any) => {
          const role = msg.role === "assistant" ? "AI" : "User";
          return `${role}: ${msg.content}`;
        }).join("\n\n---\n\n")
      : "";

    // Detect current domain for callback
    const currentDomain = getCurrentDomain();
    console.log("Detected domain for webhook callback:", currentDomain);

    // Format messages to ensure they match the expected structure
    const formattedMessages = conversation.messages 
      ? conversation.messages.map(msg => ({
          role: msg.role || 'user',
          content: String(msg.content || '')
        }))
      : [];

    // Call Supabase Edge Function instead of direct webhook
    console.log("Calling mystery-webhook-trigger Edge Function");
    const { data: webhookResponse, error: webhookError } = await supabase.functions.invoke(
      'mystery-webhook-trigger',
      {
        body: {
          conversationId: mysteryId,
          testMode: testMode
        }
      }
    );

    // Resolve packageId now if it wasn't already known -- a brand-new
    // conversation has no row until claim_package_for_generation creates one
    // inside the call above, win or lose.
    if (!packageId) {
      const { data: claimedPackage } = await supabase
        .from("mystery_packages")
        .select("id")
        .eq("conversation_id", mysteryId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      packageId = claimedPackage?.id;
    }

    if (webhookError) {
      console.error("Edge Function error:", webhookError);

      // supabase.functions.invoke() throws FunctionsHttpError on any non-2xx
      // response, discarding the status code and JSON body by default. Read
      // both off the raw Response at error.context so a rate-limit (429) or
      // already-in-progress (409) rejection isn't mislabeled as a resumable
      // mid-pipeline failure (ADR-0104 Addendum, 2026-08-23).
      let statusCode: number | undefined;
      let reason: string | undefined;
      let serverMessage: string | undefined;
      try {
        const ctx = (webhookError as any).context;
        statusCode = ctx?.status;
        if (ctx && typeof ctx.json === 'function') {
          const body = await ctx.json();
          reason = body?.reason;
          serverMessage = body?.error;
        }
      } catch (parseErr) {
        console.warn("Could not parse Edge Function error response body:", parseErr);
      }

      const isRateLimited = statusCode === 429 || reason === 'rate_limited';
      const isAlreadyGenerating = statusCode === 409 || reason === 'already_generated';

      // A 409 means the atomic claim (ADR-0104) is held by WHOEVER actually won the
      // race -- generation is genuinely in progress (or already completed) under that
      // other call, not failed. Overwriting generation_status here would stomp the
      // real status with a false "failed" for every losing caller, hiding genuine
      // progress mid-run. Found live 2026-08-23: a customer's own repeated clicks
      // while a legitimate service-role-triggered run was still active each 409'd and
      // clobbered the row to 'failed', even though Make.com was still working.
      // Leave the row untouched and just inform the caller; only a call that actually
      // owns the run should ever write to generation_status.
      if (!isAlreadyGenerating) {
        await supabase
          .from("mystery_packages")
          .update({
            generation_status: {
              status: 'failed',
              progress: 0,
              currentStep: isRateLimited
                ? 'Too many generation attempts in the last hour — please wait or contact support'
                : 'Failed to trigger generation',
              error: serverMessage || webhookError.message,
              // Only a genuine mid-pipeline failure has saved progress to resume --
              // a rate-limit rejection never started a new run, so "Resume
              // Generation" framing would be misleading.
              resumable: !isRateLimited
            },
            updated_at: new Date().toISOString()
          })
          .eq("id", packageId);
      }

      if (isRateLimited) {
        throw new Error("Too many generation attempts for this mystery in the last hour. Please wait a bit and try again, or contact support.");
      }
      if (isAlreadyGenerating) {
        throw new Error("This mystery is already generating or has already been generated.");
      }
      throw new Error(`Failed to trigger generation: ${serverMessage || webhookError.message}`);
    }

    console.log("Edge Function response:", webhookResponse);

    // Concept-completeness entry gate (ADR-0043): the edge function returns 200
    // with { success:false, reason:'needs_more_info' } and did NOT send to Make.com
    // when the conversation has no finished concept (no CHARACTER LIST message).
    // Don't overwrite that with an in_progress status — surface it as a distinct
    // sentinel so the UI can ask the customer to finish their concept.
    if (webhookResponse && webhookResponse.success === false && webhookResponse.reason === 'needs_more_info') {
      console.warn("Generation gated — concept incomplete:", webhookResponse.message);
      await supabase
        .from("mystery_packages")
        .update({
          generation_status: {
            status: 'needs_more_info',
            progress: 0,
            currentStep: 'We need a bit more detail before we can build your mystery',
            error: 'concept_incomplete',
            resumable: true,
          },
          updated_at: new Date().toISOString()
        })
        .eq("id", packageId);
      return "needs_more_info";
    }

    // Order preflight hold (ADR-0170, ADR-0178): the trigger answered success:false and stored generation_status 'held_for_review'
    // on the package itself. Do not overwrite it with in_progress; the game page shows "submitted to our team".
    if (webhookResponse && webhookResponse.success === false && webhookResponse.reason === 'held_for_review') {
      console.warn("Generation held for review:", webhookResponse.message);
      return "held_for_review";
    }

    // Update status to indicate webhook was sent successfully
    await supabase
      .from("mystery_packages")
      .update({
        generation_status: {
          status: 'in_progress',
          progress: 20,
          currentStep: 'Processing by external service...'
        },
        updated_at: new Date().toISOString()
      })
      .eq("id", packageId);
  } catch (e) {
    console.error("Unexpected error in generateCompletePackage:", e);

    // Update status to failed with error details (only if we have a packageId)
    if (packageId) {
      await supabase
        .from("mystery_packages")
        .update({
          generation_status: {
            status: 'failed',
            progress: 0,
            currentStep: 'Unexpected error during generation',
            error: e instanceof Error ? e.message : String(e),
            resumable: true
          },
          updated_at: new Date().toISOString()
        })
        .eq("id", packageId);
    }

    throw new Error(`Package generation failed: ${e instanceof Error ? e.message : String(e)}`);
  }
  
  // If we get here, the webhook was sent successfully
  return "Package generation started successfully";
}

// Simplified resume function - just calls generate again
export async function resumePackageGeneration(mysteryId: string): Promise<string> {
  console.log("Resuming package generation by calling generateCompletePackage");
  return generateCompletePackage(mysteryId);
}

// Enhanced Get generation status with content-based completion detection
export async function getPackageGenerationStatus(mysteryId: string): Promise<GenerationStatus> {
  console.log("🔍 [STATUS CHECK] getPackageGenerationStatus called for:", mysteryId);
  
  try {
    // Enhanced database query to include content fields, character count, and expected characters
    const { data, error } = await supabase
      .from("mystery_packages")
      .select(`
        generation_status,
        generation_completed_at,
        generation_started_at,
        title,
        host_guide,
        extracted_characters,
        characters:mystery_characters(count)
      `)
      .eq("conversation_id", mysteryId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
      
    if (error) {
      console.error("❌ [STATUS CHECK] Error fetching generation status:", error);
      throw new Error(`Failed to fetch generation status: ${error.message}`);
    }
    
    console.log("📊 [STATUS CHECK] Raw package data received:", data);
    
    if (!data) {
      console.log("ℹ️ [STATUS CHECK] No mystery package found, returning default not_started status");
      const defaultStatus = {
        status: 'not_started' as const,
        progress: 0,
        currentStep: 'Not started',
        sections: {}
      };
      console.log("📊 [STATUS CHECK] Returning default status:", defaultStatus);
      return defaultStatus;
    }
    
    // Content-based completion detection with character count validation
    const hasContent = !!(data.title && data.host_guide);
    const generatedCharacterCount = data.characters?.[0]?.count ?? 0;
    const hasCharacters = generatedCharacterCount > 0;

    // Parse expected character count from extracted_characters
    let expectedCharacterCount = 0;
    if (data.extracted_characters) {
      try {
        const extracted = typeof data.extracted_characters === 'string'
          ? JSON.parse(data.extracted_characters)
          : data.extracted_characters;
        expectedCharacterCount = Array.isArray(extracted) ? extracted.length : 0;
      } catch {
        expectedCharacterCount = 0;
      }
    }

    // Cross-validate against player_count from conversations table
    const { data: convRecord } = await supabase
      .from("conversations")
      .select("player_count")
      .eq("id", mysteryId)
      .maybeSingle();
    const statusPlayerCount = convRecord?.player_count || 0;
    const statusMinFromPlayerCount = statusPlayerCount > 0 ? statusPlayerCount - 2 : 0;
    const statusEffectiveExpected = Math.max(expectedCharacterCount, statusMinFromPlayerCount);

    const allCharactersGenerated = statusEffectiveExpected === 0 || generatedCharacterCount >= statusEffectiveExpected;
    const contentComplete = hasContent && hasCharacters && allCharactersGenerated;

    console.log("🔍 [STATUS CHECK] Content check results:");
    console.log("  - Has title and host_guide:", hasContent);
    console.log("  - Has characters:", hasCharacters, `(generated: ${generatedCharacterCount}, expected: ${expectedCharacterCount})`);
    console.log("  - All characters generated:", allCharactersGenerated);
    console.log("  - Content complete:", contentComplete);
    
    // Check current status in database
    let currentStatus = data.generation_status;
    console.log("📊 [STATUS CHECK] Current database status:", currentStatus);
    
    // Auto-correction logic: if content exists but status is wrong, correct it
    // ADR-0138: never auto-complete a package that is deliberately held for the quality review (the DB releases it).
    if (contentComplete && currentStatus && currentStatus.status !== 'completed' && currentStatus.status !== 'reviewing' && currentStatus.status !== 'held_for_review') {
      console.log("🔧 [STATUS CHECK] Content exists but status is not 'completed', auto-correcting...");
      
      const completedStatus = {
        status: 'completed' as const,
        progress: 100,
        currentStep: 'Package generation completed',
        sections: {
          hostGuide: true,
          characters: true,
          clues: true,
          inspectorScript: true,
          characterMatrix: true
        }
      };
      
      // Update the database with corrected status
      const { error: updateError } = await supabase
        .from("mystery_packages")
        .update({
          generation_status: completedStatus,
          generation_completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq("conversation_id", mysteryId);
      
      if (updateError) {
        console.error("❌ [STATUS CHECK] Error updating corrected status:", updateError);
      } else {
        console.log("✅ [STATUS CHECK] Status auto-corrected to 'completed'");
        currentStatus = completedStatus;
      }
    }
    
    // Check if generation_status exists and is valid
    if (!currentStatus || typeof currentStatus !== 'object') {
      console.log("ℹ️ [STATUS CHECK] No valid generation_status found, checking completion dates and content");
      
      // Fallback: check completion dates and content to determine status
      if ((data.generation_completed_at && allCharactersGenerated) || contentComplete) {
        const completedStatus = {
          status: 'completed' as const,
          progress: 100,
          currentStep: 'Package generation completed',
          sections: {
            hostGuide: true,
            characters: true,
            clues: true,
            inspectorScript: true,
            characterMatrix: true
          }
        };
        console.log("✅ [STATUS CHECK] Inferring completed status from completion date or content:", completedStatus);
        return completedStatus;
      } else if (data.generation_started_at) {
        const inProgressStatus = {
          status: 'in_progress' as const,
          progress: 50,
          currentStep: 'Package generation in progress...',
          startedAt: data.generation_started_at,
          sections: {}
        };
        console.log("🔄 [STATUS CHECK] Inferring in_progress status from start date:", inProgressStatus);
        return inProgressStatus;
      }
      
      // No dates found, return not started
      const notStartedStatus = {
        status: 'not_started' as const,
        progress: 0,
        currentStep: 'Not started',
        sections: {}
      };
      console.log("📊 [STATUS CHECK] No dates found, returning not_started:", notStartedStatus);
      return notStartedStatus;
    }
    
    const status = currentStatus as GenerationStatus;
    console.log("✅ [STATUS CHECK] Returning generation status:", status);

    // Completion invariant (ADR-0043): never REPORT a package as completed when it
    // has zero character rows. This function already refuses to *upgrade* to
    // completed without characters (contentComplete requires hasCharacters), but a
    // row whose stored status is already 'completed' was returned verbatim — the
    // path that let the "Victorian mansion - 32 Players" empty package surface as
    // done and set has_complete_package=true on load. Downgrade to in_progress at
    // read time so the UI shows the "We're Finalizing" fallback, not fake-complete.
    // The stored 'completed' row is deliberately left untouched so the
    // completed-but-empty health-check detector still flags it for triage.
    if (status.status === 'completed' && !hasCharacters) {
      console.warn(`⚠️ [STATUS CHECK] Package for ${mysteryId} is stored 'completed' with 0 characters — overriding to in_progress (ADR-0043 completion invariant)`);
      return {
        status: 'in_progress' as const,
        progress: 90,
        currentStep: 'Finalizing your characters...',
        startedAt: data.generation_started_at,
        sections: status.sections || {},
      };
    }

    // Ensure status has all required fields
    const normalizedStatus: GenerationStatus = {
      status: status.status || 'not_started',
      progress: status.progress || 0,
      currentStep: status.currentStep || 'Unknown step',
      startedAt: data.generation_started_at,
      resumable: status.resumable,
      sections: status.sections || {}
    };
    
    console.log("📊 [STATUS CHECK] Normalized status:", normalizedStatus);
    return normalizedStatus;
  } catch (error) {
    console.error("❌ [STATUS CHECK] Error in getPackageGenerationStatus:", error);
    
    // Return a safe fallback status on error
    const errorStatus = {
      status: 'not_started' as const,
      progress: 0,
      currentStep: 'Error checking status',
      sections: {}
    };
    console.log("📊 [STATUS CHECK] Returning error fallback status:", errorStatus);
    return errorStatus;
  }
}

// Allowlist of editable fields on mystery_packages
const EDITABLE_PACKAGE_FIELDS = [
  'game_overview',
  'host_guide',
  'materials',
  'preparation_instructions',
  'timeline',
  'hosting_tips',
  'evidence_cards',
  'detective_script',
] as const;

// Allowlist of editable fields on mystery_characters
const EDITABLE_CHARACTER_FIELDS = [
  'character_name',
  'description',
  'background',
  'relationships',
  'rumors',
  'secret',
  'introduction',
  // Detective-style (mystery_style = 'detective') characters store their rounds in
  // *_script / final_statement, not the innocent/guilty/accomplice branches below.
  // accusations is shown to every style. All are rendered with an edit pencil in
  // MysteryPackageTabView, so each must be allowlisted or the save throws.
  'round2_script',
  'round3_script',
  'round4_script',
  'final_statement',
  'accusations',
  'round2_questions',
  'round2_innocent',
  'round2_guilty',
  'round2_accomplice',
  'round3_questions',
  'round3_innocent',
  'round3_guilty',
  'round3_accomplice',
  'round4_questions',
  'round4_innocent',
  'round4_guilty',
  'round4_accomplice',
  'final_innocent',
  'final_guilty',
  'final_accomplice',
  'reveal_confession_guilty',
  'reveal_confession_accomplice',
] as const;

// The point-form twin of each prose field above that has one. With script_type
// 'pointForm' (or 'both') the host edits these columns, so they must be allowlisted
// too (they were not: edits were silently written to the prose column instead).
const POINTFORM_SOURCE_FIELDS = [
  'introduction',
  'rumors',
  'accusations',
  'round2_script',
  'round3_script',
  'round4_script',
  'final_statement',
  'round2_innocent',
  'round2_guilty',
  'round2_accomplice',
  'round3_innocent',
  'round3_guilty',
  'round3_accomplice',
  'round4_innocent',
  'round4_guilty',
  'round4_accomplice',
  'final_innocent',
  'final_guilty',
  'final_accomplice',
  'reveal_confession_guilty',
  'reveal_confession_accomplice',
] as const;

const EDITABLE_CHARACTER_FIELD_SET: ReadonlySet<string> = new Set<string>([
  ...EDITABLE_CHARACTER_FIELDS,
  ...POINTFORM_SOURCE_FIELDS.map((f) => `${f}_pointform`),
]);

/**
 * Update a single field on a mystery_packages row.
 */
export async function updatePackageField(
  packageId: string,
  fieldName: string,
  value: string
): Promise<void> {
  if (!EDITABLE_PACKAGE_FIELDS.includes(fieldName as any)) {
    throw new Error(`Field "${fieldName}" is not editable`);
  }

  const { error } = await supabase
    .from('mystery_packages')
    .update({
      [fieldName]: value,
      updated_at: new Date().toISOString(),
    })
    .eq('id', packageId);

  if (error) {
    console.error('Failed to update package field:', error);
    throw new Error(`Failed to save changes: ${error.message}`);
  }
}

/**
 * Update a single field on a mystery_characters row.
 */
export async function updateCharacterField(
  characterId: string,
  fieldName: string,
  value: string
): Promise<void> {
  if (!EDITABLE_CHARACTER_FIELD_SET.has(fieldName)) {
    throw new Error(`Field "${fieldName}" is not editable`);
  }

  const { error } = await supabase
    .from('mystery_characters')
    .update({
      [fieldName]: value,
      updated_at: new Date().toISOString(),
    })
    .eq('id', characterId);

  if (error) {
    console.error('Failed to update character field:', error);
    throw new Error(`Failed to save changes: ${error.message}`);
  }
}
